import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Briefcase, Users, Hammer, Clock, Percent, CalendarDays, Building2 } from 'lucide-react';
import { calculerCoutJournalier } from '@/utils/coutsSalariaux';

// Type du membre
interface Membre {
  id: string;
  prenom: string;
  nom: string;
  poste: string;
  specialite: string;
  taux_horaire: number;
  coefficient_charges: number;
  heures_par_jour: number;
  statut: 'sur_chantier' | 'disponible' | 'repos' | 'en_arret' | 'en_rotation';
  equipe_id?: string | null;
  date_fin_statut?: string | null;
}

interface MemberInfosTabProps {
  membre: Membre;
  equipeNom?: string;
  affectationActive?: {
    chantier: string;
    dateFin: string;
    isRetard: boolean;
  };
}

// Badges de statut avec couleurs
const getStatutBadge = (statut: Membre['statut']) => {
  switch (statut) {
    case 'sur_chantier':
      return { label: 'Sur chantier', variant: 'default' as const, className: 'bg-amber-500/20 text-amber-400 border-amber-500/30' };
    case 'disponible':
      return { label: 'Disponible', variant: 'default' as const, className: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' };
    case 'repos':
      return { label: 'Repos', variant: 'default' as const, className: 'bg-blue-500/20 text-blue-400 border-blue-500/30' };
    case 'en_arret':
      return { label: 'En arrêt', variant: 'default' as const, className: 'bg-red-500/20 text-red-400 border-red-500/30' };
    case 'en_rotation':
      return { label: 'En rotation', variant: 'default' as const, className: 'bg-purple-500/20 text-purple-400 border-purple-500/30' };
    default:
      return { label: 'Inconnu', variant: 'outline' as const, className: '' };
  }
};

/**
 * Onglet Informations du membre (lecture seule)
 */
export function MemberInfosTab({
  membre,
  equipeNom,
  affectationActive,
}: MemberInfosTabProps) {
  const statutBadge = getStatutBadge(membre.statut);
  const coutJournalier = calculerCoutJournalier(membre.taux_horaire, membre.coefficient_charges, membre.heures_par_jour);

  return (
    <div className="space-y-4 p-1">
      {/* Statut actuel */}
      <Card className="bg-muted/30 border-muted">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground text-sm">Statut actuel</span>
            </div>
            <Badge className={statutBadge.className}>
              {statutBadge.label}
            </Badge>
          </div>

          {/* Date fin si repos/arrêt */}
          {(membre.statut === 'repos' || membre.statut === 'en_arret') && membre.date_fin_statut && (
            <div className="mt-2 text-sm text-muted-foreground">
              Retour prévu le{' '}
              <span className="text-foreground font-medium">
                {new Date(membre.date_fin_statut).toLocaleDateString('fr-FR', {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric',
                })}
              </span>
            </div>
          )}

          {/* Affectation active */}
          {affectationActive && (
            <div className="mt-3 pt-3 border-t border-muted">
              <div className="flex items-center gap-2 text-sm">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Chantier actuel :</span>
                <span className={`font-medium ${affectationActive.isRetard ? 'text-amber-400' : 'text-emerald-400'}`}>
                  {affectationActive.chantier}
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm mt-1">
                <CalendarDays className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Jusqu'au :</span>
                <span className="text-foreground">
                  {new Date(affectationActive.dateFin).toLocaleDateString('fr-FR', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric',
                  })}
                </span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Infos professionnelles */}
      <div className="grid grid-cols-2 gap-4">
        {/* Poste */}
        <Card className="bg-muted/30 border-muted">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
              <Briefcase className="h-4 w-4" />
              Poste
            </div>
            <div className="font-medium">{membre.poste || '—'}</div>
          </CardContent>
        </Card>

        {/* Spécialité */}
        <Card className="bg-muted/30 border-muted">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
              <Hammer className="h-4 w-4" />
              Spécialité
            </div>
            <div className="font-medium">{membre.specialite || '—'}</div>
          </CardContent>
        </Card>

        {/* Équipe */}
        <Card className="bg-muted/30 border-muted">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
              <Users className="h-4 w-4" />
              Équipe
            </div>
            <div className="font-medium">{equipeNom || 'Aucune équipe'}</div>
          </CardContent>
        </Card>

        {/* Taux horaire net */}
        <Card className="bg-muted/30 border-muted">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
              <Clock className="h-4 w-4" />
              Taux net horaire
            </div>
            <div className="font-medium font-mono">{membre.taux_horaire.toFixed(2)} €/h</div>
          </CardContent>
        </Card>
      </div>

      {/* Coefficient et coût */}
      <Card className="bg-muted/30 border-muted">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                <Percent className="h-4 w-4" />
                Coefficient charges
              </div>
              <div className="font-medium">×{membre.coefficient_charges.toFixed(2)}</div>
            </div>
            <div className="text-right">
              <div className="text-muted-foreground text-sm mb-1">Coût journalier</div>
              <div className="font-bold text-sky-400 text-lg font-mono">
                {coutJournalier.toFixed(2)} €
              </div>
              <div className="text-muted-foreground text-xs">
                ({membre.heures_par_jour}h × {membre.taux_horaire.toFixed(2)}€ × {membre.coefficient_charges.toFixed(2)})
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
